import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Filter from './Filter'
import RadioFilter from './RadioFilter'

type BedBathFilterProps = {
  beds?: number
  baths?: number
  inline?: boolean
  onDone?: (beds: number, baths: number) => void
  setBeds?: (beds: number) => void
  setBaths?: (baths: number) => void
}

const formatButtonLabel = (
  t: (key: string, vars?: object) => string,
  beds: number,
  baths: number
) => {
  if (!beds && !baths) {
    return t('ui.any-beds-baths-label')
  }

  return t('ui.bd-ba-label', { beds, baths })
}

const BedBathFilter: FC<BedBathFilterProps> = ({
  beds = 0,
  baths = 0,
  inline = false,
  onDone = () => null,
  setBeds = () => null,
  setBaths = () => null,
}) => {
  const { t } = useTranslation('common')

  const [label, setLabel] = useState<string>(formatButtonLabel(t, beds, baths))
  const [localBeds, setLocalBeds] = useState<number>(beds)
  const [localBaths, setLocalBaths] = useState<number>(baths)

  const bedOptions = [
    {
      value: 0,
      label: t('ui.any'),
    },
    {
      value: 1,
      label: '1+',
    },
    {
      value: 2,
      label: '2+',
    },
    {
      value: 3,
      label: '3+',
    },
    {
      value: 4,
      label: '4+',
    },
    {
      value: 5,
      label: '5+',
    },
  ]
  const bathOptions = [
    {
      value: 0,
      label: t('ui.any'),
    },
    {
      value: 1,
      label: '1+',
    },
    {
      value: 1.5,
      label: '1.5+',
    },
    {
      value: 2,
      label: '2+',
    },
    {
      value: 3,
      label: '3+',
    },
    {
      value: 4,
      label: '4+',
    },
  ]

  const onClose = () => {
    setLabel(formatButtonLabel(t, localBeds, localBaths))
    onDone(localBeds, localBaths)
  }

  useEffect(() => {
    setLocalBeds(beds)
    setLocalBaths(baths)
    setLabel(formatButtonLabel(t, beds, baths))
  }, [beds, baths, t])

  return inline ? (
    <div className="space-y-16">
      <fieldset>
        <legend id="bedrooms-filter" className="util-label">
          {t('ui.bedrooms')}
        </legend>
        <RadioFilter
          ariaLabelledBy="bedrooms-filter"
          className="mt-8"
          items={bedOptions}
          selectedValue={beds}
          onValueChange={(value: string) => setBeds(Number(value))}
        />
      </fieldset>
      <fieldset>
        <legend id="bathrooms-filter" className="util-label">
          {t('ui.bathrooms')}
        </legend>
        <RadioFilter
          ariaLabelledBy="bathrooms-filter"
          className="mt-8"
          items={bathOptions}
          selectedValue={baths}
          onValueChange={(value: string) => setBaths(Number(value))}
        />
      </fieldset>
    </div>
  ) : (
    <Filter
      buttonText={label}
      onOpenChange={(open) => (!open ? onClose() : null)}
      onClear={() => {
        setLocalBeds(0)
        setLocalBaths(0)
      }}
      isActive={!!beds || !!baths}
    >
      <div className="space-y-16">
        <fieldset>
          <legend id="bedrooms-filter" className="util-label">
            {t('ui.bedrooms')}
          </legend>
          <RadioFilter
            ariaLabelledBy="bedrooms-filter"
            className="mt-8"
            items={bedOptions}
            selectedValue={localBeds}
            onValueChange={(value: string) => setLocalBeds(Number(value))}
          />
        </fieldset>
        <fieldset>
          <legend id="bathrooms-filter" className="util-label">
            {t('ui.bathrooms')}
          </legend>
          <RadioFilter
            ariaLabelledBy="bathrooms-filter"
            className="mt-8"
            items={bathOptions}
            selectedValue={localBaths}
            onValueChange={(value: string) => setLocalBaths(Number(value))}
          />
        </fieldset>
      </div>
    </Filter>
  )
}

export default BedBathFilter
